import { memo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Box,
  Chip,
  Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { themeColors } from '../../../theme/theme';
import { useTranslation } from '@/kit/useTranslation';

const AddMoneyDialog = memo(({ open, onClose, onAdd }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const quickAmounts = [500, 1000, 2000, 5000];

  const handleAdd = () => {
    if (amount && parseFloat(amount) > 0) {
      onAdd(parseFloat(amount));
      setAmount('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {t('payment.wallet.addMoney')}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close add money dialog">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            label={t('payment.wallet.amount')}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
            placeholder={t('payment.wallet.enterAmount')}
            sx={{ mt: 1 }}
          />

          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              {t('payment.wallet.quickAdd')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {quickAmounts.map((amt) => (
                <Chip
                  key={amt}
                  label={`₹${amt}`}
                  onClick={() => setAmount(amt.toString())}
                  sx={{
                    bgcolor: parseFloat(amount) === amt ? themeColors.primaryColor : '#f5f5f5',
                    color: parseFloat(amount) === amt ? 'white' : 'text.primary',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: parseFloat(amount) === amt ? themeColors.primaryColor : '#eeeeee',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('payment.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!amount || parseFloat(amount) <= 0}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {t('payment.wallet.addMoney')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

AddMoneyDialog.displayName = 'AddMoneyDialog';

export default AddMoneyDialog;
